/**
 * Utilities for grouping professionals by country for display in the UI.
 *
 * This module contains application-level logic for organizing professionals
 * by country, determining display order based on user location, and deciding
 * when to show country labels.
 */

import {
    SupportedCountries,
    type SupportedCountry,
    countriesList,
} from '@archipro-website/localisation';

/**
 * Display country codes include supported countries plus "Other" for unsupported ones
 */
type DisplayCountryCode = SupportedCountry | 'Other';

/**
 * Country labels for display
 */
const COUNTRY_LABELS: Record<DisplayCountryCode, string> = {
    NZ: countriesList.NZ.name,
    AU: countriesList.AU.name,
    US: countriesList.US.name,
    Other: 'Other Countries',
};

/**
 * Type guard to check if a string is a supported country code
 */
function isSupportedCountryCode(code: string): code is SupportedCountry {
    return SupportedCountries.includes(code as SupportedCountry);
}

/**
 * Determines the order in which country sections should be displayed based on the user's current country.
 * Places the user's current country first, then remaining countries alphabetically, then "Other" last.
 *
 * @param currentCountryCode - The country code where the user is currently browsing from
 * @returns Array of country codes in the order they should be displayed
 */
function getCountryOrder(currentCountryCode?: string): DisplayCountryCode[] {
    const normalizedCode = currentCountryCode?.toUpperCase();

    // Determine the user's country (fallback to NZ if unknown or unsupported)
    const userCountry: SupportedCountry =
        normalizedCode && isSupportedCountryCode(normalizedCode)
            ? normalizedCode
            : 'NZ';

    // Get all other supported countries and sort alphabetically
    const otherCountries = SupportedCountries.filter(
        (country) => country !== userCountry
    ).sort();

    // User's country first, then others alphabetically, then "Other" at the end
    return [userCountry, ...otherCountries, 'Other'];
}

/**
 * Section containing professionals grouped by country
 */
export interface ProfessionalSection<T> {
    country: DisplayCountryCode;
    label: string;
    professionals: T[];
}

/**
 * Groups professionals by their country and sorts the country groups
 * based on the user's current country. The current country appears first,
 * creating a visual gestalt grouping effect.
 *
 * @param professionals - Array of professionals to group (must have a 'country' property)
 * @param currentCountryCode - The country code where the user is currently browsing from
 * @returns Object containing grouped sections and a flag indicating if labels should be shown
 */
export function groupProfessionalsByCountry<
    T extends { country?: string | null },
>(
    professionals: T[],
    currentCountryCode?: string
): {
    sections: ProfessionalSection<T>[];
    showCountryLabels: boolean;
} {
    // Build initial grouping object dynamically from SupportedCountries
    const initialGrouped = [...SupportedCountries, 'Other' as const].reduce(
        (acc, country) => {
            acc[country] = [];
            return acc;
        },
        {} as Record<DisplayCountryCode, T[]>
    );

    // Group professionals by country
    const grouped = professionals.reduce<Record<DisplayCountryCode, T[]>>(
        (acc, professional) => {
            const raw = professional.country?.toUpperCase();
            // If country is not specified or not recognized, group under "Other"
            const country: DisplayCountryCode =
                raw && isSupportedCountryCode(raw) ? raw : 'Other';
            acc[country].push(professional);
            return acc;
        },
        initialGrouped
    );

    // Get the order based on current country
    const countryOrder = getCountryOrder(currentCountryCode);

    // Build sections in the correct order, filtering out empty sections
    const sections = countryOrder
        .map((country) => ({
            country,
            label: COUNTRY_LABELS[country],
            professionals: grouped[country],
        }))
        .filter((section) => section.professionals.length > 0);

    // Only show country labels when there are multiple countries
    const showCountryLabels = sections.length > 1;

    return {
        sections,
        showCountryLabels,
    };
}
